import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote, Home as HomeIcon, Layers, PaintRoller, Landmark, Blocks, Hammer } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useReveal } from "../hooks/useReveal";
import Seo, { LocalBusinessSchema } from "../components/Seo";

const iconMap = { Home: HomeIcon, Layers, PaintRoller, Landmark, Blocks, Hammer };

const Hero = () => {
  const { heroSlides, company } = useContent();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), 10400);
    return () => clearInterval(t);
  }, [heroSlides.length]);
  return (
    <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden">
      {heroSlides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={slide.image}
            alt={slide.heading}
            className={`w-full h-full object-cover ${i === index ? "ken-burns-slow" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-black/25 to-black/60" />
        </div>
      ))}

      <div className="relative h-full max-w-[1240px] mx-auto px-5 md:px-8 flex items-center justify-end">
        <div className="w-full md:w-[52%] lg:w-[46%] text-right">
          {heroSlides.map((slide, i) => (
            <div key={i} className={`${i === index ? "block fade-in" : "hidden"}`}>
              <p className="eyebrow !text-[#e6d3c6] mb-5">{company.tagline}</p>
              <h1 className="font-display text-white font-light text-[44px] md:text-[62px] lg:text-[72px] leading-[1.02]">
                {slide.heading}
              </h1>
              <p className="font-body text-white/85 text-[15px] md:text-[16px] leading-[1.9] mt-7 ml-auto max-w-[440px]">
                {slide.body}
              </p>
              <div className="flex justify-end gap-4 mt-9">
                <Link to="/contact" className="btn-outline">Request a consultation</Link>
                <Link to="/gallery" className="btn-primary !bg-[#94664f] hover:!bg-white hover:!text-[#23211e]">Our work</Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 flex gap-3 z-10">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className={`h-[3px] transition-all duration-500 ${i === index ? "w-10 bg-white" : "w-6 bg-white/40"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

const Intro = () => {
  const { introText, stats, company } = useContent();
  const r = useReveal();
  return (
    <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32">
      <div ref={r.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 ${r.className}`}>
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">{introText.eyebrow}</p>
          <h2 className="section-title text-[34px] md:text-[46px]">{introText.title}</h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
          {introText.paragraphs.map((p, i) => (
            <p key={i} className="font-body text-[15.5px] leading-[2] text-[#5b564d] mb-5">{p}</p>
          ))}
          <Link to="/about" className="inline-flex items-center gap-2 mt-3 text-[#23211e] font-body uppercase tracking-[0.16em] text-[12px] hover:text-[#94664f] transition-colors group">
            More about us <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 pt-16 border-t border-[#eae5db]">
        {stats.map((s, i) => (
          <div key={i} className="text-center md:text-left">
            {s.badge ? (
              <img src={company.apmBadge} alt="Association for Project Management - Full Member (MAPM)" className="h-16 md:h-[74px] w-auto mx-auto md:mx-0" />
            ) : (
              <div className="font-display text-[#94664f] text-[52px] md:text-[64px] leading-none font-light">{s.value}</div>
            )}
            <div className="overline mt-3">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const ServicesPreview = () => {
  const { services } = useContent();
  const r = useReveal();
  return (
    <section className="bg-[#f6f2ec] py-24 md:py-32">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <div ref={r.ref} className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 ${r.className}`}>
          <div>
            <p className="eyebrow mb-5">What we do</p>
            <h2 className="section-title text-[34px] md:text-[48px] max-w-[520px]">A complete range of building services under one roof</h2>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-[#23211e] font-body uppercase tracking-[0.16em] text-[12px] hover:text-[#94664f] transition-colors group whitespace-nowrap">
            All services <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e6e0d5]">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || HomeIcon;
            return (
              <Link to="/services" key={s.slug} className="group bg-[#f6f2ec] p-9 md:p-11 hover:bg-white transition-colors duration-500">
                <Icon size={30} strokeWidth={1.1} className="text-[#94664f]" />
                <h3 className="font-display text-[26px] md:text-[28px] text-[#23211e] mt-6 mb-3">{s.title}</h3>
                <p className="font-body text-[14.5px] leading-[1.9] text-[#6b665d]">{s.short}</p>
                <span className="inline-flex items-center gap-1.5 mt-6 text-[#94664f] font-body uppercase tracking-[0.14em] text-[11px] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500">
                  Learn more <ArrowUpRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FeaturedProjects = () => {
  const { projects } = useContent();
  const r = useReveal();
  const featured = projects.slice(0, 4);
  return (
    <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32">
      <div ref={r.ref} className={`text-center mb-16 ${r.className}`}>
        <p className="eyebrow mb-5">Selected work</p>
        <h2 className="section-title text-[34px] md:text-[48px]">Recent projects</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {featured.map((p, i) => (
          <Link to="/gallery" key={p.id} className={`group relative overflow-hidden ${i % 3 === 0 ? "md:col-span-2" : ""}`}>
            <div className={`overflow-hidden ${i % 3 === 0 ? "h-[380px] md:h-[520px]" : "h-[380px]"}`}>
              <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-90" />
            <div className="absolute bottom-0 left-0 p-8">
              <span className="overline !text-[#e6d3c6]">{p.category} &middot; {p.location}</span>
              <h3 className="font-display text-white text-[28px] md:text-[34px] mt-1">{p.title}</h3>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Process = () => {
  const { process } = useContent();
  const r = useReveal();
  return (
    <section className="bg-[#1c1a17] py-24 md:py-32">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <div ref={r.ref} className={`mb-16 ${r.className}`}>
          <p className="eyebrow mb-5">How we work</p>
          <h2 className="section-title !text-white text-[34px] md:text-[48px] max-w-[560px]">A considered, transparent approach from start to finish</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#332f2a]">
          {process.map((p) => (
            <div key={p.step} className="bg-[#1c1a17] p-9">
              <div className="font-display text-[#94664f] text-[46px] font-light leading-none">{p.step}</div>
              <h3 className="font-display text-white text-[24px] mt-5 mb-3">{p.title}</h3>
              <p className="font-body text-[14px] leading-[1.9] text-[#a49e94]">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { testimonials } = useContent();
  const r = useReveal();
  return (
    <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32">
      <div ref={r.ref} className={`text-center mb-16 ${r.className}`}>
        <p className="eyebrow mb-5">Client testimonials</p>
        <h2 className="section-title text-[34px] md:text-[48px]">Trusted by homeowners &amp; estates</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonials.map((t, i) => (
          <div key={i} className="border border-[#eae5db] p-9 flex flex-col">
            <Quote size={30} strokeWidth={1} className="text-[#94664f]" />
            <p className="font-display italic text-[21px] leading-[1.6] text-[#3a3833] mt-5 mb-8">&ldquo;{t.text}&rdquo;</p>
            <div className="mt-auto">
              <div className="font-body text-[14px] text-[#23211e] tracking-[0.05em]">{t.name}</div>
              <div className="overline mt-1">{t.location}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const CTA = () => (
  <section className="relative py-28 md:py-36 overflow-hidden">
    <img src="https://images.unsplash.com/photo-1696091197440-e31bd79988f6" alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-[#1c1a17]/80" />
    <div className="relative max-w-[820px] mx-auto px-5 md:px-8 text-center">
      <h2 className="section-title !text-white text-[36px] md:text-[54px]">Ready to start your project?</h2>
      <p className="font-body text-white/80 text-[16px] leading-[1.9] mt-6 max-w-[560px] mx-auto">
        Whether you are planning a new build, an extension or the sensitive restoration of a period home, we would be delighted to discuss your requirements &mdash; and to show you what integrity, professionalism and true quality look like.
      </p>
      <Link to="/contact" className="btn-primary !bg-[#94664f] hover:!bg-white hover:!text-[#23211e] mt-9">
        Get in touch <ArrowRight size={15} />
      </Link>
    </div>
  </section>
);

const Home = () => (
  <main className="pt-[76px]">
    <Seo
      description="Fletcher & Stone are traditional building contractors with over 25 years' experience delivering fine new builds, extensions, refurbishment and heritage restoration across London, The Home Counties and The Cotswolds."
      path="/"
    />
    <LocalBusinessSchema />
    <Hero />
    <Intro />
    <ServicesPreview />
    <FeaturedProjects />
    <Process />
    <Testimonials />
    <CTA />
  </main>
);

export default Home;
